// The Avenue School/the-avenue-school/client/src/containers/EventsFeedStylized.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import EventsSection from "../components/EventsSection2";
import { API_BASE } from "../lib/apiBase";

/**
 * EventsFeedStylized (container)
 *
 * Props:
 * - initialView: "upcoming" | "past"   (default: "upcoming")
 * - upcomingOnly: boolean               (default: false)
 * - margin: string | number             (default: "0") e.g. "50px" or "2rem"
 *
 * Behavior:
 * - Fetches from `${API_BASE}/events`
 * - Splits into upcoming/past
 * - Sorts upcoming asc, past desc
 */
const EventsFeedStylized = ({
  initialView = "upcoming",
  upcomingOnly = false,
  margin = "0", // NEW prop
}) => {
  const [view, setView] = useState(initialView);
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setErr(null);
        const res = await axios.get(`${API_BASE}/events`, { withCredentials: true });
        if (!alive) return;
        setAllEvents(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!alive) return;
        console.error("Error fetching events:", e);
        setErr("Unable to load events right now.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchEvents();
    return () => {
      alive = false;
    };
  }, []);

  const today = useMemo(() => moment(), []);

  const upcoming = useMemo(
    () =>
      allEvents
        .filter((ev) => moment(ev.date).isSameOrAfter(today, "day"))
        .sort((a, b) => moment(a.date).diff(moment(b.date))),
    [allEvents, today]
  );

  const past = useMemo(
    () =>
      allEvents
        .filter((ev) => moment(ev.date).isBefore(today, "day"))
        .sort((a, b) => moment(b.date).diff(moment(a.date))),
    [allEvents, today]
  );

  const mapToSection = (list) =>
    list.map((e) => ({
      day: moment(e.date).format("D"),
      mon: moment(e.date).format("MMM"),
      title: e.title || "(Untitled event)",
      href: e._id ? `/events/${e._id}` : "#", // TODO: update if route differs
    }));

  const effectiveView = upcomingOnly ? "upcoming" : view;
  const activeList =
    effectiveView === "upcoming" ? mapToSection(upcoming) : mapToSection(past);
  const title = effectiveView === "upcoming" ? "Upcoming Events" : "Past Events";

  return (
    <div style={{ margin }}>
      {/* Toggle hidden when upcomingOnly is true */}
      {!upcomingOnly && (
        <div style={{ display: "flex", justifyContent: "center", gap: 12, marginBottom: 12 }}>
          <button
            type="button"
            onClick={() => setView("upcoming")}
            aria-pressed={effectiveView === "upcoming"}
            style={{
              padding: "8px 14px",
              borderRadius: 999,
              border: "1px solid #c8d9c8",
              background: effectiveView === "upcoming" ? "#e8f2e8" : "#fff",
              cursor: "pointer",
            }}
          >
            Upcoming
          </button>
          <button
            type="button"
            onClick={() => setView("past")}
            aria-pressed={effectiveView === "past"}
            style={{
              padding: "8px 14px",
              borderRadius: 999,
              border: "1px solid #c8d9c8",
              background: effectiveView === "past" ? "#e8f2e8" : "#fff",
              cursor: "pointer",
            }}
          >
            Past
          </button>
        </div>
      )}

      {loading && (
        <div className="events" style={{ paddingTop: 32, paddingBottom: 32 }}>
          <div className="events-empty" role="status" aria-live="polite">
            Loading events…
          </div>
        </div>
      )}

      {!loading && err && (
        <div className="events" style={{ paddingTop: 32, paddingBottom: 32 }}>
          <div className="events-empty" role="alert">
            {err}
          </div>
        </div>
      )}

      {!loading && !err && <EventsSection title={title} events={activeList} />}
    </div>
  );
};

export default EventsFeedStylized;


